import Link from 'next/link'
import { getImageUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import { stripHtml } from '@/lib/strip-html'

export type TagInfoCardData = {
  name: string
  slug: string
  image?: string | null
  /** CDN — kart görseli */
  imageUrl?: string | null
  content?: string | null
}

type TagInfoCardProps = TagInfoCardData & {
  variant?: 'panel' | 'popover'
  className?: string
}

export function hasTagInfoCardData(data: TagInfoCardData) {
  return Boolean(data.image?.trim() || data.content?.trim())
}

export default function TagInfoCard({
  name,
  slug,
  image,
  imageUrl,
  content,
  variant = 'panel',
  className = '',
}: TagInfoCardProps) {
  const resolvedImageUrl =
    imageUrl?.trim() ||
    (image?.trim() ? getImageUrl(image, IMAGE_PRESETS.tagInfo) : '')
  const isPopover = variant === 'popover'
  const plainContent = content?.trim() ? stripHtml(content) : ''

  return (
    <div
      className={`overflow-hidden ${
        isPopover
          ? 'rounded-xl border border-gray-200/80 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900'
          : 'rounded-none border-0 bg-transparent shadow-none'
      } ${className}`}
    >
      {resolvedImageUrl ? (
        <div
          className={`flex w-full items-center justify-center overflow-hidden bg-gray-50/90 dark:bg-slate-800/50 ${
            isPopover ? 'min-h-[5.5rem] max-h-[8.5rem] p-2' : 'min-h-[7rem] max-h-[11rem] p-2.5 sm:p-3'
          }`}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={resolvedImageUrl}
            alt={name}
            className={`block w-full object-contain object-center ${
              isPopover ? 'max-h-[7.5rem]' : 'max-h-[10rem] sm:max-h-[10.5rem]'
            }`}
            loading="lazy"
            decoding="async"
          />
        </div>
      ) : null}

      <div className={isPopover ? 'p-3' : 'p-4 sm:p-5'}>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Etiket
        </div>
        <h2
          className={`mt-1 font-bold text-gray-900 dark:text-gray-100 ${
            isPopover ? 'text-base' : 'text-lg sm:text-xl'
          }`}
        >
          {name}
        </h2>
        {plainContent ? (
          <p
            className={`mt-2 leading-relaxed text-gray-600 dark:text-gray-300 ${
              isPopover ? 'line-clamp-5 text-xs' : 'text-sm'
            }`}
          >
            {plainContent}
          </p>
        ) : null}
        {!isPopover ? (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Bu etiketle ilişkili güncel haberler aşağıda listelenir.
          </p>
        ) : (
          <Link
            href={`/haberleri/${slug}`}
            className="mt-3 inline-flex text-xs font-semibold text-red-600 hover:text-red-700 dark:text-red-400"
          >
            Tüm {name} haberleri →
          </Link>
        )}
      </div>
    </div>
  )
}
